import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

// Cookie helper functions
function setCookie(name, value, days = 1) {
  let expires = "";
  let maxAge = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
    maxAge = "; max-age=" + (days * 24 * 60 * 60);
  }
  const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
  
  let domainStr = "";
  const hostname = window.location.hostname;
  if (hostname.endsWith("hklaw.sa")) {
    domainStr = "; domain=.hklaw.sa";
  } else if (hostname.endsWith("hukm.cloud")) {
    domainStr = "; domain=.hukm.cloud";
  }

  const cookieStr = name + "=" + encodeURIComponent(value || "") + expires + maxAge + "; path=/; SameSite=Lax" + domainStr + secureFlag;
  // console.log("DEBUG setCookie: Writing cookie string:", cookieStr);
  try {
    document.cookie = cookieStr;
  } catch (e) {
    // console.error("DEBUG setCookie: Exception setting cookie:", e);
  }
  // console.log("DEBUG setCookie: document.cookie now contains:", document.cookie);
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      } catch (e) {
        return c.substring(nameEQ.length, c.length);
      }
    }
  }
  return null;
}

function eraseCookie(name) {
  const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
  let domainStr = "";
  const hostname = window.location.hostname;
  if (hostname.endsWith("hklaw.sa")) {
    domainStr = "; domain=.hklaw.sa";
  } else if (hostname.endsWith("hukm.cloud")) {
    domainStr = "; domain=.hukm.cloud";
  }
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax' + domainStr + secureFlag;
}

// Monkey-patch window.localStorage for 'user', 'token', 'virtualCourtUser', and 'virtualCourtToken'
const originalGetItem = window.localStorage.getItem;
const originalSetItem = window.localStorage.setItem;
const originalRemoveItem = window.localStorage.removeItem;

window.localStorage.getItem = function(key) {
  if (key === 'user') {
    const userStr = getCookie('user');
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        if (userObj) {
          const cachedImage = originalGetItem.call(window.localStorage, 'profile_image');
          if (cachedImage) {
            userObj.profile_image = cachedImage;
          }
          return JSON.stringify(userObj);
        }
      } catch (e) {
        // console.error("DEBUG getItem parse error:", e);
      }
    }
    return userStr;
  }
  if (key === 'virtualCourtUser') {
    const userStr = getCookie('vc_user');
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        if (userObj) {
          const cachedImage = originalGetItem.call(window.localStorage, 'vc_profile_image');
          if (cachedImage) {
            userObj.profile_image = cachedImage;
          }
          return JSON.stringify(userObj);
        }
      } catch (e) {
        console.error("DEBUG getItem vc_user parse error:", e);
      }
    }
    return userStr;
  }
  if (key === 'token') {
    return getCookie('csrf_token');
  }
  if (key === 'virtualCourtToken') {
    return getCookie('vc_csrf_token');
  }
  return originalGetItem.call(window.localStorage, key);
};

window.localStorage.setItem = function(key, value) {
  if (key === 'user') {
    try {
      const userObj = JSON.parse(value);
      if (userObj) {
        if (userObj.profile_image) {
          originalSetItem.call(window.localStorage, 'profile_image', userObj.profile_image);
          userObj.profile_image = null;
        } else {
          originalRemoveItem.call(window.localStorage, 'profile_image');
        }
        value = JSON.stringify(userObj);
      }
    } catch (e) {
      console.error("DEBUG setItem parse error:", e);
    }
    setCookie('user', value, 1);
    return;
  }
  if (key === 'virtualCourtUser') {
    try {
      const userObj = JSON.parse(value);
      if (userObj) {
        if (userObj.profile_image) {
          originalSetItem.call(window.localStorage, 'vc_profile_image', userObj.profile_image);
          userObj.profile_image = null;
        } else {
          originalRemoveItem.call(window.localStorage, 'vc_profile_image');
        }
        value = JSON.stringify(userObj);
      }
    } catch (e) {
      console.error("DEBUG setItem vc_user parse error:", e);
    }
    setCookie('vc_user', value, 1);
    return;
  }
  if (key === 'token') {
    setCookie('csrf_token', value, 1);
    return;
  }
  if (key === 'virtualCourtToken') {
    setCookie('vc_csrf_token', value, 1);
    return;
  }
  return originalSetItem.call(window.localStorage, key, value);
};

window.localStorage.removeItem = function(key) {
  if (key === 'user') {
    eraseCookie('user');
    originalRemoveItem.call(window.localStorage, 'profile_image');
    return;
  }
  if (key === 'virtualCourtUser') {
    eraseCookie('vc_user');
    originalRemoveItem.call(window.localStorage, 'vc_profile_image');
    return;
  }
  if (key === 'token') {
    eraseCookie('csrf_token');
    eraseCookie('access_token');
    return;
  }
  if (key === 'virtualCourtToken') {
    eraseCookie('vc_csrf_token');
    eraseCookie('vc_access_token');
    return;
  }
  return originalRemoveItem.call(window.localStorage, key);
};

// Axios defaults and interceptors
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const vcCsrf = getCookie('vc_csrf_token');
  const csrf = getCookie('csrf_token');
  if (vcCsrf) {
    config.headers['X-VC-CSRF-Token'] = vcCsrf;
  }
  if (csrf) {
    config.headers['X-CSRF-Token'] = csrf;
  }
  if (!config.headers['X-CSRF-Token'] && vcCsrf) {
    config.headers['X-CSRF-Token'] = vcCsrf;
  }
  if (!config.headers['X-VC-CSRF-Token'] && csrf) {
    config.headers['X-VC-CSRF-Token'] = csrf;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Fetch wrapper for credentials and CSRF
const originalFetch = window.fetch;
window.fetch = async function (url, options = {}) {
  options.credentials = options.credentials || 'include';
  options.headers = options.headers || {};
  const vcCsrf = getCookie('vc_csrf_token');
  const csrf = getCookie('csrf_token');
  
  const setHeader = (name, value) => {
    if (options.headers instanceof Headers) {
      options.headers.set(name, value);
    } else if (Array.isArray(options.headers)) {
      const index = options.headers.findIndex(h => h[0].toLowerCase() === name.toLowerCase());
      if (index === -1) {
        options.headers.push([name, value]);
      } else {
        options.headers[index][1] = value;
      }
    } else {
      options.headers[name] = value;
    }
  };

  if (vcCsrf) {
    setHeader('X-VC-CSRF-Token', vcCsrf);
  }
  if (csrf) {
    setHeader('X-CSRF-Token', csrf);
  }
  if (vcCsrf && !csrf) {
    setHeader('X-CSRF-Token', vcCsrf);
  }
  if (csrf && !vcCsrf) {
    setHeader('X-VC-CSRF-Token', csrf);
  }
  
  return originalFetch(url, options);
};

// Render React Application
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);
